import React, { useState, useRef, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import styles from './Media.module.scss';
import cx from 'classnames';
import Modal from '../Modal';
import { ICONS_PATH } from '../../../lib/constants';
import Icon from '../Icon';
import { getYoutubeVideoIdFromUrl } from '../../../lib/helpers/mediaHeplers';
import { FaqItem } from '../../lazyComponents';
import { Text } from '../../index';
import { t } from '@konrad/reweb-aem';
import { linkService } from '@konrad/reweb-aem';
import { isCrossOrigin } from '../../../lib/helpers/uri';
import { LINK_TYPES } from '../../../lib/constants';

const Media = (props) => {
    const [loaded, setLoaded] = useState(false);
    const videoButtonRef = useRef();
    const hasModalOpen = useRef(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
        altText,
        videoLink,
        fileReference,
        horizontalAlignment,
        graphicalElement,
        className,
        videoTranscription,
        showTranscription,
        linkUrl,
        linkType,
        linkAltText,
    } = props;
    const isVideo = videoLink && videoLink !== 'undefined';
    let videoId = '';
    let videoUrl = '';
    let videoBtnText = isModalOpen ? '' : 'Open';
    const videoContainerStyles = {};
    let url = linkUrl ? linkService.get(linkUrl) : null;
    if (isVideo) {
        if (fileReference) videoContainerStyles.backgroundImage = `url('${fileReference}')`;
        if (videoLink) {
            const ytVideoId = getYoutubeVideoIdFromUrl(videoLink);
            if (ytVideoId) {
                videoId = 'fs-media-video-modal' + (altText && altText.trim().split(' ').join('-'));
                videoUrl = `https://www.youtube.com/embed/${ytVideoId}?rel=0`;
            }
        }
    }
    const openVideo = () => {
        hasModalOpen.current = true;
        setIsModalOpen(true);
    };
    const closeVideo = () => setIsModalOpen(false);

    useEffect(() => {
        if (!isModalOpen && hasModalOpen.current) {
            videoButtonRef.current.focus();
            hasModalOpen.current = false;
        }
    }, [isModalOpen]);

    return (
        <div
            className={cx(
                styles.wrapper,
                styles[`wrapper--${graphicalElement}`],
                styles[`h-alignment--${horizontalAlignment}`],
                className
            )}
        >
            {isVideo ? (
                <>
                    <div
                        className={cx(
                            styles['video-wrapper'],
                            styles[`decoration--${graphicalElement}`]
                        )}
                    >
                        <div
                            onLoad={() => setLoaded(true)}
                            className={cx(styles['video-container'], className)}
                            style={videoContainerStyles}
                        >
                            <button
                                ref={videoButtonRef}
                                aria-expanded="false"
                                aria-controls={videoId}
                                aria-label={videoBtnText}
                                onClick={openVideo}
                                className={styles['play-btn']}
                            >
                                <Icon
                                    className={styles['play-btn__icon']}
                                    size="small"
                                    url={ICONS_PATH + 'symbol-play-video.svg'}
                                />
                            </button>
                        </div>
                        <Modal id={videoId} modalState={isModalOpen} handleClose={closeVideo}>
                            <div className={styles['modal-body']}>
                                <iframe
                                    allowFullScreen={true}
                                    allow="accelerometer; gyroscope; picture-in-picture"
                                    src={videoUrl}
                                    frameBorder="0"
                                    title={altText}
                                    width="100%"
                                    height="100%"
                                />
                            </div>
                        </Modal>
                    </div>

                    {showTranscription && videoTranscription ? (
                        <Suspense fallback={''}>
                            <FaqItem question={t('fs.media.video-transcription')}>
                                <Text
                                    className={styles['transcription']}
                                    content={videoTranscription}
                                    size="body"
                                />
                            </FaqItem>
                        </Suspense>
                    ) : null}
                </>
            ) : linkUrl ? (
                <a
                    href={url}
                    aria-label={linkAltText}
                    rel={isCrossOrigin(url, linkType) ? 'noopener' : null}
                    target={linkType === 'external' ? '_blank' : undefined}
                >
                    <div
                        className={cx(
                            styles['image-wrapper'],
                            styles[`decoration--${graphicalElement}`]
                        )}
                    >
                        {!loaded && (
                            <div
                                className={cx(
                                    styles.img,
                                    className,
                                    styles['img--center'],
                                    styles['img__placeholder'],
                                    loaded ? styles['img--hidden'] : ''
                                )}
                            />
                        )}
                        <img
                            onLoad={() => setLoaded(true)}
                            className={cx(styles.img, className)}
                            alt={altText || ''}
                            src={fileReference}
                            loading="lazy"
                        />
                    </div>
                </a>
            ) : (
                <div
                    className={cx(
                        styles['image-wrapper'],
                        styles[`decoration--${graphicalElement}`]
                    )}
                >
                    {!loaded && (
                        <div
                            className={cx(
                                styles.img,
                                className,
                                styles['img--center'],
                                styles['img__placeholder'],
                                loaded ? styles['img--hidden'] : ''
                            )}
                        />
                    )}
                    <img
                        onLoad={() => setLoaded(true)}
                        className={cx(styles.img, className)}
                        alt={altText || ''}
                        src={fileReference}
                        loading="lazy"
                    />
                </div>
            )}
        </div>
    );
};

Media.displayName = 'Media';

Media.propTypes = {
    altText: PropTypes.string,
    videoLink: PropTypes.string,
    fileReference: PropTypes.string,
    graphicalElement: PropTypes.string,
    horizontalAlignment: PropTypes.string,
    linkAltText: PropTypes.string,
    linkType: PropTypes.oneOf(LINK_TYPES),
    linkUrl: PropTypes.string,
};

export default Media;
